import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import Box from '@/components/ui/Box/Box';
import EmailField from '@/components/ui/EmailField/EmailField';
import Grid from '@/components/ui/Grid/Grid';
import Link from '@/components/ui/Link/Link';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton';
import PasswordField from '@/components/ui/PasswordField/PasswordField';
import Typography from '@/components/ui/Typography/Typography';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';
import type { LoginFormValues, ApiError } from './ILoginPage';
import logo from '@/assets/images/icon.svg';
import { styles } from './styles';

const initialValues: LoginFormValues = { email: '', password: '' };

const validationSchema = yup.object().shape({
    email: yup.string().email('invalid email').required('required'),
    password: yup.string().required('required'),
});

const LoginPage = () => {
    const [loading, setLoading] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);

    const navigate = useNavigate();
    const auth = useAuth();
    const errorContext = useError();

    const onLoginError = (error: unknown) => {
        setLoading(false);
        const apiErr = error as ApiError;
        if (apiErr?.status === 401) {
            apiErr.message = 'Invalid Email or Password. Please try again.';
        }
        errorContext.DISPATCH({ type: 'API_ERROR', payload: apiErr });
    };

    const onLoginSuccess = () => {
        setLoading(false);
    };

    const handleSubmit = ({ email, password }: LoginFormValues) => {
        setLoading(true);
        void auth.actions.login(email, password, onLoginSuccess, onLoginError);
    };

    const isReadyToSubmit = (errors: { email?: string; password?: string }) =>
        emailTouched && !errors.email && passwordTouched && !errors.password;

    return (
        <>
            <Grid container item direction="column" alignItems="center" justifyContent="center">
                <img src={logo} style={styles.logoImg} alt="Repair Exchange" />
                <Typography align="center" variant="h1" sx={styles.logoText}>
                    Sign In
                </Typography>
            </Grid>
            <Formik
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange
                validateOnBlur={false}
            >
                {({ values, errors, touched, handleBlur, handleChange, handleSubmit: submit }) => (
                    <form onSubmit={submit} style={{ minWidth: '100%' }}>
                        <Box sx={styles.formStack}>
                            <EmailField
                                id="outlined-email"
                                name="email"
                                label="Enter Email"
                                value={values.email}
                                error={!!touched.email && !!errors.email}
                                helperText={touched.email ? errors.email : ''}
                                disabled={loading}
                                onChange={(e) => {
                                    setEmailTouched(true);
                                    handleChange(e);
                                }}
                                onBlur={handleBlur}
                            />
                            <PasswordField
                                id="outlined-adornment-password"
                                name="password"
                                label="Enter Password"
                                value={values.password}
                                error={!!touched.password && !!errors.password}
                                helperText={touched.password ? errors.password : ''}
                                disabled={loading}
                                onChange={(e) => {
                                    setPasswordTouched(true);
                                    handleChange(e);
                                }}
                                onBlur={handleBlur}
                            />
                            <LoadingButton
                                type="submit"
                                color="secondary"
                                variant={isReadyToSubmit(errors) ? 'contained' : 'outlined'}
                                loading={loading}
                                fullWidth
                            >
                                SIGN IN
                            </LoadingButton>
                            <Link
                                component="button"
                                type="button"
                                aria-disabled={loading}
                                sx={loading ? styles.forgotPasswordDisabled : styles.forgotPassword}
                                onClick={() => {
                                    if (loading) return;
                                    navigate('forgot_password');
                                }}
                            >
                                Forgot Password?
                            </Link>
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default LoginPage;
